import { useSelector } from 'react-redux';
import type { LabelType } from '@common/customControls/getServiceLabel';
import type { ReportCategories } from '../../../../common/enums/reportCategories';
import type { IReduxState } from '../../../configureStore';
import type { IDiagnosticReportGetOne } from '../../interfaces/diagnosticReportGetOne';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { ISupervisionGetOne } from '../../interfaces/supervisionGetOne';
import type { ITask } from '../../interfaces/task';
import { useEpisodeContext } from '../../../utils/hooks/useEpisodeContext';
import { ReloadEpisode } from '../../pages/episodes/reloadEpisode';
import { ButtonContainer } from '../common';
import { AddClarificationButton } from './AddClarificationButton';
import { AddLetterOfDischargeIntakeReportButton } from './AddLetterOfDischargeIntakeReportButton';
import { BecomeRequesterButton } from './BecomeRequesterButton';
import { ConfirmDiagnosticReportButton } from './ConfirmDiagnosticReportButton';
import { ConvertToConsultButton } from './ConvertToConsultButton';
import { ConvertToReferralButton } from './ConvertToReferralButton';
import { EditDiagnosticReportButton } from './EditDiagnosticReportButton';
import { PrintCarePathButton } from './PrintCarePathButton';
import { RemoveDiagnosticReportButton } from './RemoveDiagnosticReportButton';
import { TakeDiagnosticReportButton } from './TakeDiagnosticReportButton';
import { TakeSupervisionButton } from './TakeSupervisionButton';
import { UpdateSupervisionButton } from './UpdateSupervisionButton';

interface IDiagnosticReportReadButtonsProps {
    acceptDiagnosticReport?: () => Promise<void>;
    addClarification?: () => Promise<void>;
    addDiagnosticReport?: (category: ReportCategories) => Promise<void>;
    customButtonsLeft?: JSX.Element[];
    diagnosticReport: IDiagnosticReportGetOne;
    editDiagnosticReport?: () => Promise<void>;
    isLastItem: boolean;
    isPreviousRequest?: boolean;
    reloadProcess?: (reload?: ReloadEpisode) => Promise<void>;
    removeDiagnosticReport?: () => Promise<void>;
    request: IRequestGetOne;
    requestMessageToGP?: boolean;
    supervision?: ISupervisionGetOne;
    task?: ITask;
    toastServiceLabelType?: LabelType;
    updateSupervision?: () => Promise<void>;
}

export function DiagnosticReportReadButtons(props: IDiagnosticReportReadButtonsProps) {
    const { patient, onConvertToConsult, onConvertToReferral } = useEpisodeContext();
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const customButtonsLeft = props.customButtonsLeft ?? [];

    if (!props.isLastItem || isManager) {
        return null;
    }

    return (
        <ButtonContainer
            alignRight
            buttons={[
                <PrintCarePathButton
                    isLastItem={props.isLastItem}
                    isPreviousRequest={props.isPreviousRequest}
                    request={props.request}
                />,
                ...customButtonsLeft,
                <RemoveDiagnosticReportButton
                    diagnosticReport={props.diagnosticReport}
                    removeDiagnosticReport={props.removeDiagnosticReport}
                    serviceCode={props.request.service.code}
                    toastServiceLabelType={props.toastServiceLabelType}
                />,
                <AddClarificationButton
                    addClarification={props.addClarification}
                    diagnosticReport={props.diagnosticReport}
                />,
                <EditDiagnosticReportButton
                    diagnosticReport={props.diagnosticReport}
                    editDiagnosticReport={props.editDiagnosticReport}
                    request={props.request}
                />,
                <TakeDiagnosticReportButton
                    diagnosticReport={props.diagnosticReport}
                    reloadProcess={props.reloadProcess}
                />,
                <BecomeRequesterButton
                    diagnosticReport={props.diagnosticReport}
                    reloadProcess={props.reloadProcess}
                    request={props.request}
                />,
                <AddLetterOfDischargeIntakeReportButton
                    addReport={props.addDiagnosticReport}
                    request={props.request}
                />,
                <ConvertToConsultButton
                    acceptDiagnosticReport={props.acceptDiagnosticReport}
                    diagnosticReport={props.diagnosticReport}
                    isPreviousRequest={props.isPreviousRequest}
                    onConvertToConsult={onConvertToConsult}
                    patient={patient}
                    request={props.request}
                    serviceCode={props.request.service.code}
                    serviceId={props.request.serviceId}
                    serviceType={props.request.serviceType}
                />,
                <ConvertToReferralButton
                    acceptDiagnosticReport={props.acceptDiagnosticReport}
                    diagnosticReport={props.diagnosticReport}
                    isPreviousRequest={props.isPreviousRequest}
                    onConvertToReferral={onConvertToReferral}
                    patient={patient}
                    request={props.request}
                    serviceCode={props.request.service.code}
                    serviceId={props.request.serviceId}
                    serviceType={props.request.serviceType}
                />,
                <ConfirmDiagnosticReportButton
                    acceptDiagnosticReport={props.acceptDiagnosticReport}
                    diagnosticReport={props.diagnosticReport}
                    patient={patient}
                    reloadAll={() => {
                        if (props.reloadProcess) {
                            props.reloadProcess(ReloadEpisode.reloadAll);
                        }
                    }}
                    request={props.request}
                    requestMessageToGP={props.requestMessageToGP}
                    serviceCode={props.request.service.code}
                />,
                <TakeSupervisionButton
                    practitionerId={practitionerId}
                    reloadProcess={props.reloadProcess}
                    supervision={props.supervision}
                    task={props.task}
                />,
                <UpdateSupervisionButton
                    practitionerId={practitionerId}
                    task={props.task}
                    updateSupervision={props.updateSupervision}
                />,
            ]}
        />
    );
}
